import { inject } from '@angular/core';
import { CanActivateFn, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { AuthService } from '@services/auth.service';
import { AppLoggerService } from '@services/app-logger.service';
import { UserSessionService, activeSessionErrors } from '@services/user-session.service';
import { EventManagerService } from '@services/event-manager.service';
import { defaultConfig } from '../../app.config';

const { noSessionNext, sessionEnded, restartApp } = defaultConfig.appEvents;

/**
 * Guard de autenticación.
 * 1. Verifica tokens OAuth2 → si no hay, redirige a auth-ui (Hydra)
 * 2. Verifica sesión de backend → si no hay, redirige a post-auth
 */
export const authGuard: CanActivateFn = async (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const authService = inject(AuthService);
  const logger = inject(AppLoggerService);
  const userSessionService = inject(UserSessionService);
  const eventManager = inject(EventManagerService);
  const router = inject(Router);

  const requestedRoute = state.url;
  logger.debug('[AUTH GUARD] Verificando autenticación...');

  // Paso 1: Verificar tokens OAuth2
  if (!authService.isAuthenticated()) {
    logger.debug('[AUTH GUARD] No hay tokens OAuth2 - Redirigiendo a auth-ui...');
    authService.login();
    return false;
  }

  // Paso 2: Verificar sesión de backend
  logger.debug('[AUTH GUARD] Tokens OAuth2 OK - Verificando sesión backend...');

  try {
    const errorCode = await userSessionService.activeSession();

    if (errorCode === activeSessionErrors.validSession) {
      logger.debug('[AUTH GUARD] Sesión válida - Acceso permitido');
      return true;
    }

    if (errorCode === activeSessionErrors.expiredOnServer) {
      logger.debug('[AUTH GUARD] Sesión expirada en servidor');
      eventManager.next(sessionEnded.name, requestedRoute);
    } else if (errorCode === activeSessionErrors.concurrentSession) {
      logger.debug('[AUTH GUARD] Sesión concurrente detectada');
      eventManager.next(sessionEnded.name, null);
    } else if (errorCode === activeSessionErrors.noSession) {
      logger.debug('[AUTH GUARD] No hay sesión backend - Redirigiendo a post-auth...');
      eventManager.next(noSessionNext.name, requestedRoute);
      router.navigate(['/auth/post-auth']);
      return false;
    } else if (errorCode === activeSessionErrors.missingUserFunctions) {
      logger.warn('[AUTH GUARD] Faltan permisos de usuario');
      eventManager.next(restartApp.name, null);
    } else {
      logger.error('[AUTH GUARD] Error desconocido:', errorCode);
      eventManager.next(restartApp.name, null);
    }

    return false;
  } catch (error) {
    logger.error('[AUTH GUARD] Error verificando sesión:', error);
    router.navigate(['/auth/post-auth']);
    return false;
  }

  return false;
};
